import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Table } from '@naf/table';
import { Accordion } from '@naf-simba/elements-accordion';
import { colors, simbaStyled } from '@naf-simba/theme';
import { isNotTransientOrSystemProp } from '@lib/shouldForwardProp';
import { breakpoints, spacing } from '@naf/theme';
import { Link } from '@naf-simba/elements-links';
import { nafColor } from '@nafcore/theme';
import { CheckCircle } from '@styled-icons/material/CheckCircle';

export const StyledFigure = styled.figure`
  margin: 0;
  padding: 0;
`;

export const Caption = styled.figcaption`
  font-size: 0.875rem;
  font-style: italic;
  max-width: 100%;
  @media (max-width: 768px) {
    margin-top: 10px;
  }
`;

export const ExpandedTable = styled(Table)<{ shouldCollapse: boolean }>`
  display: block;
  ${(props) =>
    props.shouldCollapse &&
    css`
      @media (max-width: 768px) {
        display: none;
      }
    `}
`;

/**
 * Transient prop: `shouldCollapse` was forwarded straight through to the Accordion, which spreads
 * unknown props onto its header `<button>`, so React warned about it on the DOM.
 *
 * `theme.breakpoints.values.md` rather than `down('md')` — MUI subtracts 0.05px, which would move
 * the boundary at exactly 768px.
 */
export const CollapsedTable = simbaStyled<typeof Accordion, { $shouldCollapse: boolean }>(Accordion, {
  shouldForwardProp: isNotTransientOrSystemProp,
})(({ theme, $shouldCollapse }) => ({
  display: 'none',
  ...($shouldCollapse && {
    [`@media (max-width: ${theme.breakpoints.values.md}px)`]: {
      display: 'block',
    },
  }),
}));

export const TableWrapper = styled.div<{ width?: number }>`
  display: flex;
  flex-direction: column;
  position: relative;
  width: ${(props) => (props.width ? `calc(${props.width}px - 96px)` : '100vw')};
  margin-bottom: ${spacing.space64};

  h2 ~ div > div {
    overflow: hidden;
  }

  @media (max-width: ${breakpoints.m}) {
    width: ${(props) => (props.width ? `calc(${props.width}px - 48px)` : '100vw')};
  }
  @media (max-width: ${breakpoints.s}) {
    width: ${(props) => (props.width ? `calc(${props.width}px)` : '100vw')};
    margin-bottom: ${spacing.space40};
  }
`;

export const StyledTable = styled.table`
  width: 100%;
  display: table;
  table-layout: fixed;
  border-spacing: 0;
  border-collapse: collapse;
  min-width: 340px;
`;

export const ShowMoreWrapper = styled.div<{ $isHidden?: boolean }>`
  max-height: 10000px;
  transition: max-height 1s ease-in;

  ${({ $isHidden }) =>
    $isHidden &&
    css`
      max-height: 0;
      overflow: hidden;
    `}
`;

// @naf/table's Td has no horizontal padding, so give cell content its own gutter to stop
// neighbouring columns' text from touching on narrow screens. overflow-wrap lets long
// unbroken strings (e.g. "Kirkenes/Hesseng") wrap onto a second line instead of overflowing.
export const CellText = styled.span`
  display: block;
  padding: 0 ${spacing.space16};
  overflow-wrap: break-word;
`;

export const Tr = styled.tr<{ index?: number }>`
  display: table-row;
  box-sizing: border-box;
  border-bottom: 1px solid ${(props) => (props.index === 0 ? nafColor.neutral.neutral3 : 'rgba(0, 0, 0, 0.12)')};

  th:nth-child(2),
  td:nth-child(2) {
    border-right: 1px solid rgba(0, 0, 0, 0.12);
    @media (max-width: ${breakpoints.s}) {
      border-right: 0;
    }
  }
`;

const StyledTableCell = css`
  display: table-cell;
  padding: ${spacing.space16} 0 ${spacing.space16} ${spacing.space80};
  text-align: left;
  box-sizing: border-box;

  @media (max-width: ${breakpoints.m}) {
    padding-left: ${spacing.space24};
  }

  @media (max-width: ${breakpoints.s}) {
    padding: ${spacing.space12} 0;
  }

  &:first-child {
    padding-left: 0;
  }
`;

export const Th = styled.th`
  ${StyledTableCell};
`;

export const Td = styled.td`
  ${StyledTableCell};
`;

export const StyledCheckCircle = styled(CheckCircle)`
  color: ${colors.secondary[500]};
`;

export const StyledButtonLink = styled(Link)`
  margin-top: ${spacing.space32};
`;
